import ensureError from "@-/com.npmjs.ensure-error";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { useCallback, useMemo, useState } from "react";
import { copyToClipboard } from "@-/dev.improve.haniverse.lib.clipboard";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  AppLayoutContext,
  type AppSelectionItem,
} from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelectionActions } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";
import {
  type UserCardActionTarget,
  useUserCardActions,
} from "@-/dev.improve.haniverse.lib.cards.user-card.actions";
import {
  useCreateUserWordCard,
  useLearnOrQueueGrapheme,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.user-card.create";
import { FREE_TIER_DAILY_NEW_GRAPHEME_LIMIT } from "@-/dev.improve.haniverse.lib.entitlement.free-tier";
import { useDailyNewGraphemeAllowanceQueryOptions } from "@-/dev.improve.haniverse.lib.learn.new-grapheme-limit";
import { useDailyNewCardsSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.daily-new-cards.navigator";
import { useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import plurCount from "@-/com.npmjs.plur-count";
import { toast } from "@-/com.npmjs.sonner";
import { useFreeTierGate } from "@-/dev.improve.haniverse.components.superwall";
import runes from "@-/com.npmjs.runes2";
import type { IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { hasMinLength } from "@-/com.npmjs.ts-array-length";

export type MorphemeSelectionOverlayAction = {
  disabled: boolean;
  icon: IconName;
  key: "learn" | "skip" | "mark-mastered" | "copy";
  label: string;
  run: (selectedItems: readonly AppSelectionItem[]) => Promise<void> | void;
};

function userCardActionTarget(item: AppSelectionItem): UserCardActionTarget {
  return item.kind === "morpheme"
    ? { front: item.front, kind: "morpheme" }
    : { kind: "user-card", userCardId: item.userCardId };
}

/**
 * Builds the action set for app-wide entity selection and runs the chosen action over a stable
 * snapshot before leaving selection mode. Morpheme targets retain the historic bundle semantics;
 * exact deck-card targets mutate only their concrete `user_card` row. Learn is unavailable for an
 * exact-card session because an already-existing card cannot be learned again.
 */
export function useMorphemeSelectionOverlayActions() {
  const [selectedItems] = useContextSelectors(AppLayoutContext, (c) => c.selectedItems);
  const { stopSelecting } = useAppSelectionActions();
  const { markMasteredMutation, skipMutation } = useUserCardActions();
  const learnOrQueueGraphemeMutation = useLearnOrQueueGrapheme();
  const createUserWordCardMutation = useCreateUserWordCard();
  const { canAddToStudy, openUpgradeToPremium } = useFreeTierGate("selection_overlay_learn");
  const queryClient = useQueryClient();
  const dailyNewGraphemeAllowanceQueryOptions = useDailyNewGraphemeAllowanceQueryOptions();
  const dailyNewCardsPageNavigator = useDailyNewCardsSettingsPageNavigator();
  const [isRunningAction, setIsRunningAction] = useState(false);

  const runAndDismiss = useCallback(
    (run: (selectedItems: readonly AppSelectionItem[]) => Promise<void> | void) => {
      if (isRunningAction) {
        return;
      }
      const selectedItemsSnapshot = [...selectedItems.values()];
      setIsRunningAction(true);
      void (async () => {
        try {
          await run(selectedItemsSnapshot);
          stopSelecting();
        } catch (error) {
          logger(import.meta.location).error(
            "[morpheme-selection] action failed",
            ensureError(error),
          );
        } finally {
          setIsRunningAction(false);
        }
      })();
    },
    [isRunningAction, selectedItems, stopSelecting],
  );

  const containsExactUserCard = [...selectedItems.values()].some(
    (item) => item.kind === "user-card",
  );

  const actions = useMemo<MorphemeSelectionOverlayAction[]>(
    () => [
      {
        disabled: containsExactUserCard,
        icon: "MdSchool",
        key: "learn",
        label: "Learn",
        // Words and graphemes take different creation paths: a word becomes a
        // `user_word_sentence_card` (drilled in sentence context), a single grapheme becomes the
        // usual meaning + pronunciation bundle. They are deliberately separate cards even when
        // the text matches, so routing on length here is the whole distinction.
        //
        // Sequential, not `Promise.all`: each call opens its own actor-DB write, matching how
        // `mark-mastered` below already batches.
        run: async (selectedItemsToLearn) => {
          // A multi-select can straddle the free boundary. Add everything that IS allowed, then
          // paywall once for the remainder — rather than refusing the whole batch, which would
          // make the free graphemes in the selection unaddable too.
          const blocked: string[] = [];
          // A learner past today's new-grapheme limit gets the rest QUEUED rather than refused: they are enrolled on a later day, and the deck's Queued section lists them.
          let queuedCount = 0;
          for (const item of selectedItemsToLearn) {
            if (item.kind !== "morpheme") {
              continue;
            }
            const { front } = item;
            if (!canAddToStudy(front)) {
              blocked.push(front);
              continue;
            }
            if (hasMinLength(runes(front), 2)) {
              await createUserWordCardMutation.mutateAsync({ word: front });
              continue;
            }
            if ((await learnOrQueueGraphemeMutation.mutateAsync(front)) === "queued") {
              queuedCount += 1;
            }
          }
          if (hasMinLength(blocked, 1)) {
            openUpgradeToPremium();
          }
          if (queuedCount > 0) {
            // The limit that queued them: the free tier's cap offers the upgrade that lifts it, the
            // learner's own Daily New Cards limit points to that setting.
            const { isFreeTierCap, limit } = await queryClient.fetchQuery(
              dailyNewGraphemeAllowanceQueryOptions,
            );
            toast(
              `Queued ${plurCount(queuedCount, "character")} for tomorrow`,
              isFreeTierCap
                ? {
                    action: { label: "Upgrade", onClick: openUpgradeToPremium },
                    description: `The free plan introduces up to ${FREE_TIER_DAILY_NEW_GRAPHEME_LIMIT} new characters a day, and you've reached today's limit. Upgrade to Premium to learn as many as you like.`,
                  }
                : {
                    action: {
                      label: "Settings",
                      onClick: () => dailyNewCardsPageNavigator.push.mutate(),
                    },
                    description: `Daily New Cards is set to ${limit.toLocaleString()}, and you've reached today's limit.`,
                  },
            );
          }
        },
      },
      {
        disabled: false,
        icon: "LuSkipForward",
        key: "skip",
        label: "Skip",
        run: async (selectedItemsToSkip) => {
          for (const item of selectedItemsToSkip) {
            await skipMutation.mutateAsync(userCardActionTarget(item));
          }
        },
      },
      {
        disabled: false,
        icon: "LuTrophy",
        key: "mark-mastered",
        label: "Mark Known",
        // Apply mastery to each selected target. Run
        // sequentially (not `Promise.all`) — each call opens its own actor-DB
        // write and they target independent `user_card` rows, so serializing
        // keeps the writes orderly without contending for the connection.
        run: async (selectedItemsToMaster) => {
          for (const item of selectedItemsToMaster) {
            await markMasteredMutation.mutateAsync(userCardActionTarget(item));
          }
        },
      },
      {
        disabled: false,
        icon: "MdContentCopy",
        key: "copy",
        label: "Copy",
        run: (selectedItemsToCopy) => {
          void copyToClipboard(selectedItemsToCopy.map((item) => item.front).join(""));
        },
      },
    ],
    [
      canAddToStudy,
      containsExactUserCard,
      createUserWordCardMutation,
      dailyNewCardsPageNavigator,
      dailyNewGraphemeAllowanceQueryOptions,
      learnOrQueueGraphemeMutation,
      markMasteredMutation,
      openUpgradeToPremium,
      queryClient,
      skipMutation,
    ],
  );

  return { actions, isRunningAction, runAndDismiss, selectedCount: selectedItems.size };
}
